import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useTheme } from "@mui/material";
import { CHART_COLORS } from "../../chartPalette";

// Paleta categórica por defecto: la misma del dashboard estático. Aplica a todo
// consumidor que no mande `colors` — hoy el visor y el editor de
// /analytics/custom-dashboards y el Builder de Exports, todos vía PivotChart.
// TrendChartCard sí la manda explícita, así que no depende de este default.
const DEFAULT_COLORS = CHART_COLORS;

/**
 * EChart — Apache ECharts equivalent of DynamicChart. Keeps the same data
 * contract so callers can swap one for the other with no data changes:
 *   chartData = { data: [{ label, count }], type, title }
 *
 * Supported `type` values: "line", "bar", "column", "hbar", "pie", "donut",
 * "nightingale" (roseType "area"), "rose" (roseType "radius"), "gauge" and
 * "treemap".
 * ("column" and "bar" both render vertical bars, matching current usage;
 * "hbar" renders horizontal bars, for categories with long labels.)
 *
 * The treemap takes its own payload instead of `data`:
 *   chartData.tree = [{ label, count, color, detail }]
 * `detail` es una línea extra que sólo aparece en el tooltip.
 *
 * The gauge takes its own payload instead of `data`:
 *   chartData.gauge = {
 *     value,                              // posición de la aguja (0–max)
 *     max = 100,
 *     bands: [{ min, max, color, grade }],// tramos coloreados del arco;
 *                                         // `grade` es el rótulo sobre el arco
 *     label,                              // etiqueta de la banda, bajo el número
 *   }
 */
export const EChart = ({
  chartData,
  type = "column",
  title,
  colors = DEFAULT_COLORS,
  opacity = 0.9,
  height = 360,
  animation = true,
}) => {
  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const secondaryText = theme.palette.text.secondary;
  const axisLine = theme.palette.divider;
  const paper = theme.palette.background.paper;

  const option = useMemo(() => {
    const data = chartData?.data ?? [];
    const categories = data.map((d) => d.label);
    const values = data.map((d) => d.count);

    const baseTooltip = {
      backgroundColor: paper,
      borderColor: theme.palette.divider,
      textStyle: { color: textColor },
    };

    // Treemap: reparte el área del lienzo entre los nodos, en proporción a su
    // valor. Sólo tiene sentido cuando los nodos son excluyentes entre sí y
    // suman el total; si se traslapan, las áreas mienten.
    //
    // Deliberadamente plano. La variante anidada depende de `upperLabel` para
    // rotular al padre, y esa franja ECharts sólo la reserva bajo condiciones
    // que en un lienzo chico no se cumplen de forma confiable: el resultado era
    // un treemap donde los bloques no decían a qué grupo pertenecían. Un solo
    // nivel siempre rotula. El desglose que aportaría el segundo nivel viaja en
    // `detail`, que se muestra en el tooltip.
    if (type === "treemap") {
      const nodes = chartData?.tree ?? [];
      if (!nodes.length) return {};

      // El tooltip necesita el detalle del nodo bajo el cursor; se resuelve por
      // nombre, que aquí es único.
      const detalles = new Map(nodes.map((n) => [n.label, n.detail]));

      return {
        tooltip: {
          ...baseTooltip,
          formatter: (info) =>
            [`<b>${info.name}</b>`, `${info.value}`, detalles.get(info.name)]
              .filter(Boolean)
              .join("<br/>"),
        },
        series: [
          {
            type: "treemap",
            // Es una foto, no un explorador: sin zoom ni drill-down.
            roam: false,
            nodeClick: false,
            breadcrumb: { show: false },
            animationDurationUpdate: 400,
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            itemStyle: { borderColor: paper, borderWidth: 2, gapWidth: 4 },
            label: {
              show: true,
              color: "#fff",
              fontSize: 12,
              lineHeight: 17,
              // `break` en vez de `truncate`: los nombres de estado son largos
              // y cortarlos deja mosaicos que no dicen nada.
              overflow: "break",
              formatter: "{b}\n{c}",
            },
            data: nodes.map((n) => ({
              name: n.label,
              value: n.count,
              itemStyle: n.color ? { color: n.color } : undefined,
            })),
          },
        ],
      };
    }

    // Grade gauge (semicírculo): un valor sobre un arco dividido en bandas de
    // color, con el rótulo de cada banda por fuera del arco y la aguja como
    // marcador. Las bandas llegan ordenadas por `max` ascendente.
    if (type === "gauge") {
      const g = chartData?.gauge;
      if (!g) return {};
      const max = g.max ?? 100;
      const bands = g.bands?.length ? g.bands : [{ max, color: colors[0] }];

      // Los rótulos sólo pueden colgarse de un tick del eje, y las bandas no
      // son de ancho uniforme: con los 8 tramos del dibujo ninguno cae en el
      // centro de su color. Se resuelve con una segunda serie —invisible, sólo
      // rótulos— sobre una rejilla fina, donde el centro de cada banda sí tiene
      // tick propio. La serie visible conserva sus 8 divisiones.
      const LABEL_SPLIT = 200;
      const labelStep = 1 / LABEL_SPLIT;
      const gradeAt = new Map();
      bands.forEach((b, i) => {
        if (!b.grade) return;
        const lo = (b.min ?? bands[i - 1]?.max ?? 0) / max;
        const hi = b.max / max;
        gradeAt.set(Math.round((lo + hi) / 2 / labelStep), b.grade);
      });

      const geometria = {
        type: "gauge",
        min: 0,
        max: 1,
        startAngle: 180,
        endAngle: 0,
        center: ["50%", "72%"],
        radius: "92%",
      };

      return {
        tooltip: {
          ...baseTooltip,
          trigger: "item",
          formatter: () => [g.label, `${g.value} / ${max}`].filter(Boolean).join("<br/>"),
        },
        series: [
          {
            ...geometria,
            splitNumber: 8,
            // Cada banda pinta su tramo del arco; los stops van normalizados.
            // `roundCap` redondea las puntas para que el gauge no desentone
            // con las barras y donas del dashboard, que van con borderRadius.
            // Redondea cada tramo, no sólo los extremos, pero con 6px de ancho
            // el cap mide 3px y el traslape en las uniones no se nota.
            axisLine: {
              roundCap: true,
              lineStyle: {
                width: 6,
                color: bands.map((b) => [Math.min(1, b.max / max), b.color]),
              },
            },
            // Mismo triángulo del ejemplo de ECharts pero con las tres puntas
            // redondeadas (curva cuadrática a 5 unidades de cada vértice), para
            // que la aguja combine con el `roundCap` del arco.
            pointer: {
              icon:
                "path://M14.23,5.49L23.37,36.01Q24.8,40.8 19.8,40.8L5.7,40.8" +
                "Q0.7,40.8 2.14,36.01L11.36,5.49Q12.8,0.7 14.23,5.49Z",
              length: "12%",
              width: 16,
              offsetCenter: [0, "-60%"],
              itemStyle: { color: "auto" },
            },
            axisTick: { length: 8, lineStyle: { color: "auto", width: 2, cap: "round" } },
            splitLine: { length: 14, lineStyle: { color: "auto", width: 4, cap: "round" } },
            axisLabel: { show: false },
            // Sólo la etiqueta de la banda, bajo el número.
            title: {
              offsetCenter: [0, "-6%"],
              color: textColor,
              fontSize: 15,
              fontWeight: 600,
            },
            detail: {
              valueAnimation: true,
              offsetCenter: [0, "-34%"],
              fontSize: 32,
              fontWeight: 700,
              color: "inherit",
              formatter: (v) => String(Math.round(v * max)),
            },
            data: [{ value: g.value / max, name: g.label ?? "" }],
          },
          {
            // Serie fantasma: aporta únicamente los rótulos, centrados en su
            // banda, siguiendo la curva y por fuera de la línea del arco
            // (`distance` negativo empuja hacia afuera).
            ...geometria,
            splitNumber: LABEL_SPLIT,
            silent: true,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            pointer: { show: false },
            anchor: { show: false },
            title: { show: false },
            detail: { show: false },
            axisLabel: {
              color: textColor,
              fontSize: 13,
              fontWeight: 600,
              distance: -42,
              rotate: "tangential",
              formatter: (v) => gradeAt.get(Math.round(v / labelStep)) ?? "",
            },
            data: [{ value: 0 }],
          },
        ],
      };
    }

    // Circular family: pie / donut / nightingale / rose
    const isCircular = ["pie", "donut", "nightingale", "rose"].includes(type);
    if (isCircular) {
      // Nested pie: `chartData.rings` renders concentric rings (e.g. outer ring
      // = status, inner ring = closure types). Each ring has its own data,
      // radius and per-slice colors.
      const rings = chartData?.rings;
      if (Array.isArray(rings) && rings.length) {
        return {
          tooltip: { ...baseTooltip, trigger: "item", formatter: "{a}<br/>{b}: {c} ({d}%)" },
          legend: { bottom: 0, type: "scroll", textStyle: { color: textColor } },
          series: rings.map((ring, ri) => ({
            name: ring.name,
            type: "pie",
            radius: ring.radius,
            center: ["50%", "45%"],
            avoidLabelOverlap: true,
            itemStyle: { borderRadius: 3, borderColor: paper, borderWidth: 2, opacity },
            // Outer ring (first) labels outside; inner rings label inside.
            label:
              ri === 0
                ? { color: textColor, formatter: "{b}\n{c}" }
                : { position: "inner", color: "#fff", fontSize: 10, formatter: "{c}" },
            labelLine: ri === 0 ? { lineStyle: { color: secondaryText } } : { show: false },
            data: (ring.data || []).map((d, i) => ({
              name: d.label,
              value: d.count,
              itemStyle: ring.colors?.[i] ? { color: ring.colors[i] } : undefined,
            })),
          })),
        };
      }

      const roseType =
        type === "nightingale" ? "area" : type === "rose" ? "radius" : undefined;
      const radius =
        type === "donut"
          ? ["45%", "70%"]
          : roseType
          ? ["15%", "75%"]
          : "70%";

      return {
        color: colors,
        tooltip: { ...baseTooltip, trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: { bottom: 0, type: "scroll", textStyle: { color: textColor } },
        series: [
          {
            name: title,
            type: "pie",
            radius,
            center: ["50%", "45%"],
            roseType,
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 4,
              borderColor: paper,
              borderWidth: 2,
              opacity,
            },
            label: { color: textColor, formatter: "{b}\n{c}" },
            labelLine: { lineStyle: { color: secondaryText } },
            data: data.map((d) => ({ name: d.label, value: d.count })),
          },
        ],
      };
    }

    // Cartesian: line / bar / column / hbar
    const isLine = type === "line";

    // Barras horizontales: para categorías con etiquetas largas, que en
    // vertical se encimarían en el eje x. El eje de categorías dibuja el
    // primer elemento abajo, así que se invierte todo para leer de arriba
    // hacia abajo en el orden recibido.
    if (type === "hbar") {
      const n = values.length;
      return {
        color: colors,
        tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: "shadow" } },
        grid: { left: 8, right: 32, bottom: 8, top: 16, containLabel: true },
        xAxis: {
          type: "value",
          axisLabel: { color: secondaryText },
          splitLine: { lineStyle: { color: axisLine, type: "dashed" } },
        },
        yAxis: {
          type: "category",
          data: [...categories].reverse(),
          axisLabel: { color: secondaryText },
          axisLine: { lineStyle: { color: axisLine } },
          axisTick: { show: false },
        },
        series: [
          {
            name: title || chartData?.title,
            type: "bar",
            data: [...values].reverse(),
            barMaxWidth: 28,
            itemStyle: {
              // `dataIndex` va sobre el arreglo invertido: se remapea para que
              // cada categoría conserve el color que le tocaba.
              color: (params) => colors[(n - 1 - params.dataIndex) % colors.length],
              borderRadius: [0, 4, 4, 0],
              opacity,
            },
            label: { show: true, position: "right", color: textColor },
          },
        ],
      };
    }

    // Multi-series: when `chartData.series` has more than one entry, overlay
    // them (e.g. one line per year to compare). Categories come from the first
    // series; every series is assumed to share the same x labels.
    const multi = Array.isArray(chartData?.series) && chartData.series.length > 1;
    if (multi) {
      const cats = chartData.series[0].data.map((d) => d.label);
      return {
        color: colors,
        tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: isLine ? "line" : "shadow" } },
        legend: { top: 0, textStyle: { color: textColor } },
        grid: { left: 8, right: 16, bottom: 8, top: 32, containLabel: true },
        xAxis: {
          type: "category",
          data: cats,
          axisLabel: { color: secondaryText },
          axisLine: { lineStyle: { color: axisLine } },
          axisTick: { show: false },
        },
        yAxis: {
          type: "value",
          axisLabel: { color: secondaryText },
          splitLine: { lineStyle: { color: axisLine, type: "dashed" } },
        },
        series: chartData.series.map((s, idx) => {
          const c = colors[idx % colors.length];
          return {
            name: s.name,
            type: isLine ? "line" : "bar",
            data: s.data.map((d) => d.count),
            smooth: isLine,
            showSymbol: isLine,
            symbolSize: 6,
            barMaxWidth: 42,
            itemStyle: { color: c, borderRadius: isLine ? 0 : [4, 4, 0, 0], opacity },
            lineStyle: isLine ? { width: 3, color: c } : undefined,
          };
        }),
      };
    }

    return {
      color: colors,
      tooltip: { ...baseTooltip, trigger: "axis", axisPointer: { type: isLine ? "line" : "shadow" } },
      grid: { left: 8, right: 16, bottom: 8, top: 24, containLabel: true },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: { color: secondaryText },
        axisLine: { lineStyle: { color: axisLine } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        axisLabel: { color: secondaryText },
        splitLine: { lineStyle: { color: axisLine, type: "dashed" } },
      },
      series: [
        {
          name: title || chartData?.title,
          type: isLine ? "line" : "bar",
          data: values,
          smooth: isLine,
          showSymbol: isLine,
          symbolSize: 7,
          barMaxWidth: 42,
          itemStyle: {
            color: isLine ? colors[0] : (params) => colors[params.dataIndex % colors.length],
            borderRadius: isLine ? 0 : [4, 4, 0, 0],
            opacity,
          },
          lineStyle: isLine ? { width: 3, color: colors[0] } : undefined,
          areaStyle: isLine ? { color: colors[0], opacity: 0.12 } : undefined,
          label: { show: true, position: "top", color: textColor },
        },
      ],
    };
  }, [chartData, type, title, colors, opacity, textColor, secondaryText, axisLine, paper, theme.palette.divider]);

  if (
    !chartData?.data?.length &&
    !chartData?.series?.length &&
    !chartData?.rings?.length &&
    !chartData?.gauge &&
    !chartData?.tree?.length
  )
    return null;

  return (
    <ReactECharts
      option={{ ...option, animation }}
      style={{ width: "100%", height }}
      opts={{ renderer: "svg" }}
      notMerge
    />
  );
};

export default EChart;
